"use client";

import { useEffect, useState } from "react";
import { Anchor, Waves, Fish } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function CountUpNumeral({
  target,
  isVisible,
  delay
}: {
  target: number;
  isVisible: boolean;
  delay: number;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    const timeout = setTimeout(() => {
      const duration = 1200;
      const start = performance.now();
      const tick = (now: number) => {
        const elapsed = now - start;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setCount(Math.floor(eased * target));
        if (progress < 1) requestAnimationFrame(tick);else setCount(target);
      };
      requestAnimationFrame(tick);
    }, delay);
    return () => clearTimeout(timeout);
  }, [isVisible, target, delay]);
  return <>{count.toString().padStart(2, "0")}</>;
}
export default function OurMission() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const principles = [{
    numeral: 1,
    icon: Anchor,
    title: "HERITAGE",
    subtitle: "BUILT BY HAND",
    body: "The Miss Yvonne was forged in 1965, a vessel defined by the grit of Ocean Springs. We honor this legacy by maintaining the same uncompromising standards of craftsmanship that have defined our family-owned operation for over half a century.",
    metric: "ESTABLISHED 1965"
  }, {
    numeral: 2,
    icon: Waves,
    title: "SUSTAINABILITY",
    subtitle: "PROVEN BY TIME",
    body: "We believe in harvesting only what the sea can provide. Our commitment to responsible, wild-caught practices ensures that the Gulf Coast remains vibrant for future generations, honoring the delicate balance of our maritime ecosystem.",
    metric: "100% WILD-CAUGHT"
  }, {
    numeral: 3,
    icon: Fish,
    title: "QUALITY",
    subtitle: "EXPERTLY CUT",
    body: "From the deck to your doorstep, every fillet is handled with precision. We bypass the middleman to ensure that the seafood you receive is as fresh as the day it was pulled from the water, delivered with care and integrity.",
    metric: "SHORE TO DOOR DELIVERY"
  }];
  return <section data-section-id="3364" id="our-mission" ref={ref} className="bg-background text-foreground py-24 md:py-36 lg:py-[180px] px-6 md:px-10 relative overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: "linear-gradient(var(--foreground) 1px, transparent 1px), linear-gradient(90deg, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "80px 80px"
    }} />

      <div className="max-w-7xl mx-auto relative">
        {}
        <div className={`text-center transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span className="font-mono text-xs md:text-sm tracking-widest text-muted-foreground uppercase">
            // 03 — Our Mission
          </span>
        </div>

        {}
        <h2 className={`font-default text-4xl md:text-6xl lg:text-7xl text-foreground text-center mt-6 md:mt-8 max-w-5xl mx-auto leading-[1.05] tracking-tight transition-all duration-700 delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Built by Hand,{" "}
          <span className="italic" style={{
          color: "var(--primary)"
        }}>
            Proven by Time.
          </span>
        </h2>

        {}
        <div className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-3 gap-0 relative">
          {principles.map((principle, i) => {
          const Icon = principle.icon;
          return <div key={i} data-index={i} className={`relative px-6 md:px-8 lg:px-10 py-10 md:py-4 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: `${200 + i * 150}ms`
          }}>
                {}
                {i > 0 && <div className="hidden md:block absolute left-0 top-0 bottom-0 w-px bg-border" />}
                {}
                {i > 0 && <div className="md:hidden absolute left-0 right-0 top-0 h-px bg-border" />}

                {}
                <div className="absolute -top-8 md:-top-12 left-4 md:left-6 font-default pointer-events-none select-none leading-none" style={{
              fontSize: "clamp(120px, 14vw, 200px)",
              color: "var(--primary)",
              opacity: 0.08
            }} aria-hidden="true">
                  <CountUpNumeral target={principle.numeral} isVisible={isVisible} delay={300 + i * 200} />
                </div>

                {}
                <div className="relative z-10">
                  {}
                  <div className={`mb-8 transition-all duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`} style={{
                transitionDelay: `${500 + i * 200}ms`
              }}>
                    <Icon className="w-10 h-10 md:w-12 md:h-12 stroke-[1.25]" style={{
                  color: "var(--primary)"
                }} strokeDasharray={isVisible ? "0" : "200"} strokeDashoffset={isVisible ? "0" : "200"} />
                  </div>

                  {}
                  <h3 className="font-default text-3xl md:text-4xl lg:text-5xl text-foreground tracking-tight leading-none">
                    {principle.title}
                  </h3>

                  {}
                  <p className="mt-3 font-mono text-[11px] md:text-xs tracking-[0.15em] text-muted-foreground uppercase">
                    {principle.subtitle}
                  </p>

                  <Separator className="mt-6 mb-6 bg-border/60" />

                  {}
                  <p className="font-default text-sm md:text-base leading-relaxed text-muted-foreground">
                    {principle.body}
                  </p>

                  {}
                  <div className="mt-8 pt-5 border-t border-border/40">
                    <p className="font-mono text-xs md:text-sm tracking-wider" style={{
                  color: "var(--primary)"
                }}>
                      {principle.metric}
                    </p>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}